/* eslint-disable react/prop-types */
/* eslint-disable max-len */
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getToClinicsOfDoctor } from 'Redux/modules/clinics/actions';
import { getSpecialist, getSpeciality } from 'Redux/modules/doctor/actions';
import {
  createReferralMemo,
  getPreviewReferralMemo,
} from 'Redux/modules/webChat/referralMemo/actions';
import { Button, Modal } from 'semantic-ui-react';
import { formatDateBody } from 'Helpers/date-utils';
import { removeModal } from 'Redux/modules/modal/actions';
import useProfile from 'Hooks/doctor/useDoctorProfile';
import { findClinicIdFromClinicsMapping } from 'Helpers/consult-utils';

import MemoContent from './MemoContent';
import PatientDetailsForm from './PatientDetailsForm';
import ReferralForm from './ReferralForm';
import css from './ReferralMemo.less';
import Step from './Step';

const initialValues = {
  patientName: '',
  patientNric: '',
  patientGender: '',
  patientDob: '',
  subject: '',
  content: '',
  fromClinicId: undefined,
  toClinicId: '',
  toDoctorId: '',
  specialistInfo: '',
  specialistEmail: '',
  referToManaDr: true,
};
const ModalConfirm = ({ setConfirm, openConfirm, setOpenConfirm }) => {
  const onClose = () => {
    setOpenConfirm(false);
    setConfirm();
  };
  const onStay = () => {
    setOpenConfirm(false);
  };
  return (
    <Modal size="tiny" open={openConfirm} className={css.memoConfirmModal}>
      <Modal.Header>Unsaved changes</Modal.Header>
      <Modal.Content>
        <div className={css.contentConfrim}>
          Changes you made may not be saved. Are you sure you wish to close?
        </div>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>Close</Button>
        <Button onClick={onStay} primary>
          Stay
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

const reformatBody = values => {
  const { referToManaDr, specialistEmail, specialistInfo, toDoctorId, toClinicId, fromClinicId } =
    values;
  return {
    ...values,
    fromClinicId,
    specialistEmail: !referToManaDr ? specialistEmail.trim() : undefined,
    specialistInfo: !referToManaDr ? specialistInfo.trim() : undefined,
    toDoctorId: referToManaDr ? toDoctorId : undefined,
    toClinicId: referToManaDr && toClinicId ? toClinicId : undefined,
    patientDob: formatDateBody(values.patientDob),
    referToManaDr: undefined,
  };
};

function ReferralMemo({ data: { conversation }, id }) {
  const [step, setStep] = useState(1);
  const [openConfirm, setOpenConfirm] = useState(false);
  const formRef = useRef(null);
  const token = useSelector(state => state.auth.data.token);
  const { clinicsTo } = useSelector(state => state.clinics);
  const { speciality, specialist } = useSelector(state => state.doctors);
  const {
    status,
    status: { referralPreview },
    error,
  } = useSelector(state => state.webChat.referralMemo);

  const { profile } = useProfile();

  const {
    latestConsult: { id: consultId, forPatient },
  } = conversation;

  const [values, setValues] = useState({
    ...initialValues,
    patientName: forPatient?.fullName,
    patientNric: forPatient?.nric,
    patientGender: forPatient?.genderAtBirth?.toLocaleLowerCase(),
    patientDob: forPatient?.dateOfBirth ? moment(forPatient?.dateOfBirth) : undefined,
    fromClinicId: findClinicIdFromClinicsMapping(
      conversation?.latestConsult?.clinics || [],
      profile.id,
    ),
  });

  const dispatch = useDispatch();
  const onClose = () => {
    dispatch(removeModal(id));
  };

  const handleGetSpecialist = specialId => {
    dispatch(getSpecialist(specialId));
  };

  const handleGetToClinicsOfDoctor = drId => {
    dispatch(getToClinicsOfDoctor(drId));
  };
  const handleNext = async () => {
    // eslint-disable-next-line no-unused-expressions
    await formRef?.current?.submitForm();
    if (formRef?.current?.isValid) {
      const newValues = {
        ...values,
        ...formRef?.current?.values,
      };
      setValues(newValues);
      if (step === 3) {
        const body = reformatBody(newValues);
        const data = await dispatch(getPreviewReferralMemo(body, token));
        setStep(step + 1);
        const iframe = document.getElementById('previewframe');
        iframe.src = `data:text/html,${encodeURIComponent(data.data)}`;
      } else {
        setStep(step + 1);
      }
    }
  };
  const handleIssueReferral = async () => {
    const body = reformatBody(values);
    await dispatch(createReferralMemo(consultId, body));
    onClose();
  };
  const handleBack = () => {
    const newValues = {
      ...values,
      ...formRef?.current?.values,
    };
    setValues(newValues);
    setStep(step - 1);
  };
  const onCloseModal = () => {
    setOpenConfirm(true);
  };

  useEffect(() => {
    dispatch(getSpeciality({ display_on_memo: true }));
  }, [dispatch]);

  return (
    <div className={css.memoModal}>
      <Modal size="small" onClose={onCloseModal} closeIcon open className={css.memoModal}>
        <Modal.Header>{step === 4 ? 'Preview' : 'Create Referral Letter'}</Modal.Header>
        <Modal.Content>
          {step !== 4 && <Step step={step} />}
          <div className={step === 4 ? css.contentPreview : css.content}>
            {step === 1 && <PatientDetailsForm ref={formRef} initialValues={values} />}
            {step === 2 && <MemoContent ref={formRef} initialValues={values} />}
            {step === 3 && (
              <ReferralForm
                specialties={speciality}
                specialist={specialist}
                ref={formRef}
                initialValues={values}
                handleGetSpecialist={handleGetSpecialist}
                handleGetToClinicsOfDoctor={handleGetToClinicsOfDoctor}
                clinicsTo={clinicsTo}
              />
            )}
            {step === 4 && <iframe title="preview" id="previewframe" className={css.iframeCss} />}
          </div>
        </Modal.Content>
        <Modal.Actions>
          {step !== 1 && <Button onClick={handleBack}>Back</Button>}
          {step === 4 ? (
            <Button loading={status.createReferralMemo === 0} onClick={handleIssueReferral} primary>
              Issue Referral
            </Button>
          ) : (
            <Button loading={referralPreview === 0} onClick={handleNext} primary>
              Continue
            </Button>
          )}
          <div className="error">{error?.data?.message}</div>
        </Modal.Actions>
      </Modal>
      <ModalConfirm
        openConfirm={openConfirm}
        setOpenConfirm={setOpenConfirm}
        setConfirm={onClose}
      />
    </div>
  );
}

export default ReferralMemo;
